import { Drawer, makeStyles, Popper, Typography, ClickAwayListener } from '@material-ui/core'
import React from 'react'
import CloseIcon from '@material-ui/icons/Close'
import Paper from '@paintscout/ui/components/Paper'
import IconButton from '@paintscout/ui/components/IconButton'
import Button from '@paintscout/ui/components/Button'
import { useIsMobile } from '@paintscout/ui/hooks'
import Hidden from '@paintscout/ui/components/Hidden'
import MobileDetailsTable from '../MobileDetailsTable'
import DetailsContainer from '../DetailsContainer/DetailsContainer'
import type { Details } from '../DetailsContainer/DetailsContainer'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'

const useStyles = makeStyles((theme) => ({
  drawer: {
    '& .MuiDrawer-paper': {
      // Ensure the drawer is flush with the bottom on iOS devices with home indicators
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      // Prevent overflow on small viewports
      maxHeight: '80vh',
      // Use CSS custom property to apply dvh where supported (iOS Safari/Chrome)
      '@supports (max-height: 80dvh)': {
        maxHeight: '80dvh'
      }
    }
  },
  root: {
    borderTop: `1px solid ${theme.palette.divider}`,
    position: 'relative',
    boxShadow: `${theme.shadows[2]} !important`
  },
  content: {
    paddingTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      '& > div': {
        boxShadow: 'none'
      },
      maxHeight: '350px',
      overflowY: 'auto'
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0px !important'
    }
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 1, 1, 2),
    [theme.breakpoints.down('sm')]: {
      position: 'sticky',
      top: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: theme.zIndex.appBar + 1
    }
  },
  closeButton: {
    padding: theme.spacing(0.5)
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      position: 'sticky',
      bottom: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: theme.zIndex.appBar + 1
    }
  },
  cancelButton: {
    minWidth: 0,
    justifyContent: 'flex-start',
    '& > span': {
      color: theme.palette.error.main
    }
  },
  detailsPaper: {
    border: 'none'
  }
}))

export interface PreviewPopperProps {
  key: string
  anchorEl: HTMLElement | null
  details: Details[]
  onClose: () => void
  onConfirm: () => void
  onCancel: () => void
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  cancelButtonDisabled?: boolean
  Footer?: React.ReactNode
  hideFooter?: boolean
  noDetailsText?: string
  minWidth?: number
}

export default function PreviewPopper({
  key,
  anchorEl,
  details,
  onClose,
  onConfirm,
  onCancel,
  title = 'Details',
  confirmLabel = 'Confirm',
  cancelLabel = 'cancel',
  cancelButtonDisabled = false,
  hideFooter = false,
  noDetailsText,
  minWidth
}: PreviewPopperProps) {
  const classes = useStyles()
  const mobile = useIsMobile()
  const open = Boolean(anchorEl)
  const anchorWidth = anchorEl ? anchorEl.getBoundingClientRect().width : undefined
  const width = mobile ? '100%' : minWidth ? Math.min(anchorWidth, minWidth) : anchorWidth

  // Prevent background scrolling while the mobile drawer is open. The Drawer uses
  // disableScrollLock, so we lock scroll ourselves via the shared hook (also masks the
  // scrollbar gutter / grey bar), matching DialogStackRenderer and EventPreviewPopper.
  useBodyScrollLock(mobile && open)

  const handleClickAway = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorEl && anchorEl.contains(event.target as Node)) {
      return
    }

    onClose()
  }

  const header = (
    <Hidden mdUp>
      <div className={classes.header}>
        <Typography variant="h3">{mobile ? title : null}</Typography>
        <IconButton onClick={onClose} size="small" className={classes.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
    </Hidden>
  )

  const footer = !hideFooter && (
    <div className={classes.footer}>
      <Button
        variant="textNoPadding"
        className={classes.cancelButton}
        onClick={onCancel}
        style={cancelButtonDisabled ? { opacity: 0.5 } : {}}
        disabled={cancelButtonDisabled}
      >
        {cancelLabel}
      </Button>
      <Button onClick={onConfirm}>{confirmLabel}</Button>
    </div>
  )

  if (mobile) {
    return (
      <Drawer anchor="bottom" open={Boolean(anchorEl)} onClose={onClose} disableScrollLock className={classes.drawer}>
        <Paper className={classes.root} style={{ minWidth: width, maxWidth: width }}>
          {header}
          <div className={classes.content}>
            <MobileDetailsTable details={details} />
          </div>
          {footer}
        </Paper>
      </Drawer>
    )
  }

  return (
    <Popper
      key={key}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      id={`preview-popper-${key}`}
      style={{ zIndex: 2000 }}
    >
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
        <div>
          <Paper className={classes.root} style={{ minWidth: width, maxWidth: width }}>
            {header}
            <div className={classes.content}>
              <DetailsContainer
                hideTitle
                details={details}
                noDetailsText={noDetailsText}
                classes={{ paper: classes.detailsPaper }}
              />
            </div>
            {footer}
          </Paper>
        </div>
      </ClickAwayListener>
    </Popper>
  )
}
